import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { APP_CONSTANTS } from '../constants/app.constants';
import { ROLE_PERMISSIONS, isSalesTeamRole } from '../permissions/role-permissions';
import { getNormalizedRoleFromStoredUserData } from '../utils/role-normalization.utils';
import { getDefaultRouteForRole, getStoredUserData } from '../utils/user-storage.utils';

export const roleGuard: CanActivateFn = (_route, state) => {
  const router = inject(Router);

  const userData = getStoredUserData();
  if (!userData) {
    router.navigate([APP_CONSTANTS.ROUTES.LOGIN]);
    return false;
  }

  let userRole = '';
  try {
    userRole = getNormalizedRoleFromStoredUserData(userData);
  } catch {
    router.navigate([APP_CONSTANTS.ROUTES.LOGIN]);
    return false;
  }

  const currentUrl = state.url.split('?')[0];

  // The CSI/CSA KPI Report child route is registered under /reports with a path
  // that already starts with "reports/", so the actual URL has /reports twice.
  // Build the real navigated URL from the existing constants for guard checks
  // and redirects.
  const csiKpiReportUrl = `${APP_CONSTANTS.ROUTES.REPORTS}${APP_CONSTANTS.ROUTES.CSI_KPI_REPORT}`;

  const permissionMap: { route: string; allowedRoles: string[] }[] = [
    { route: APP_CONSTANTS.ROUTES.DASHBOARD, allowedRoles: ROLE_PERMISSIONS.DASHBOARD },
    { route: APP_CONSTANTS.ROUTES.CALENDAR, allowedRoles: ROLE_PERMISSIONS.CALENDAR },
    { route: APP_CONSTANTS.ROUTES.STUDENT_DASHBOARD, allowedRoles: ROLE_PERMISSIONS.STUDENT_DASHBOARD },
    { route: APP_CONSTANTS.ROUTES.STUDENT_PRACTICE_MOCK_SESSION, allowedRoles: ROLE_PERMISSIONS.PRACTICE_MOCK_SESSION },
    { route: APP_CONSTANTS.ROUTES.NOTIFICATIONS, allowedRoles: ROLE_PERMISSIONS.INBOX },
    { route: APP_CONSTANTS.ROUTES.INBOX, allowedRoles: ROLE_PERMISSIONS.INBOX },
    { route: APP_CONSTANTS.ROUTES.COMPANY, allowedRoles: ROLE_PERMISSIONS.COMPANY },
    { route: APP_CONSTANTS.ROUTES.JOB, allowedRoles: ROLE_PERMISSIONS.JOB },
    { route: APP_CONSTANTS.ROUTES.APPLICATIONS, allowedRoles: ROLE_PERMISSIONS.APPLICATION },
    { route: '/applications/easy-application', allowedRoles: ROLE_PERMISSIONS.EASY_APPLICATION },
    { route: APP_CONSTANTS.ROUTES.QUICK_APPLICATION, allowedRoles: ROLE_PERMISSIONS.QUICK_APPLICATION },
    { route: '/interviews/add', allowedRoles: ROLE_PERMISSIONS.INTERVIEW_ADD },
    { route: '/interviews/edit', allowedRoles: ROLE_PERMISSIONS.INTERVIEW_EDIT },
    { route: APP_CONSTANTS.ROUTES.INTERVIEWS, allowedRoles: ROLE_PERMISSIONS.INTERVIEWS },
    // More specific report routes must be evaluated before the generic /reports rule.
    // The "child reports" container path is /reports/reports/* (an existing
    // quirk in reports.routes.ts where child paths include the "reports/" prefix).
    { route: csiKpiReportUrl, allowedRoles: ROLE_PERMISSIONS.CSI_KPI_REPORT },
    { route: `${APP_CONSTANTS.ROUTES.REPORTS}${APP_CONSTANTS.ROUTES.COMPLAINT_LIST}`, allowedRoles: ROLE_PERMISSIONS.COMPLAINT_LIST },
    { route: `${APP_CONSTANTS.ROUTES.REPORTS}${APP_CONSTANTS.ROUTES.REPORTS}`, allowedRoles: ROLE_PERMISSIONS.REPORT_DETAILS },
    { route: APP_CONSTANTS.ROUTES.REPORTS, allowedRoles: ROLE_PERMISSIONS.REPORT },
    { route: APP_CONSTANTS.ROUTES.SETTINGS_ANNOUNCEMENT, allowedRoles: ROLE_PERMISSIONS.ANNOUNCEMENTS },
    { route: APP_CONSTANTS.ROUTES.SETTINGS_BOOKING_LIMIT, allowedRoles: ROLE_PERMISSIONS.BOOKING_LIMIT },
    { route: APP_CONSTANTS.ROUTES.SETTINGS_FORMS, allowedRoles: ROLE_PERMISSIONS.ACTION_ITEM_FORMS },
    { route: APP_CONSTANTS.ROUTES.COMPANY_MERGE, allowedRoles: ROLE_PERMISSIONS.COMPANY_MERGE },
    { route: APP_CONSTANTS.ROUTES.SETTINGS, allowedRoles: ROLE_PERMISSIONS.SETTINGS },
    { route: '/students/add', allowedRoles: ROLE_PERMISSIONS.STUDENT_ADD },
    { route: '/students/edit', allowedRoles: ROLE_PERMISSIONS.STUDENT_EDIT },
    { route: '/students/manage-resumes', allowedRoles: [...ROLE_PERMISSIONS.STUDENT_ADD, APP_CONSTANTS.ROLES.CSI] },
    { route: '/students', allowedRoles: ROLE_PERMISSIONS.STUDENTS }
  ];

  // Evaluate longest (most specific) route prefixes first and stop on the first
  // match so a child rule (e.g. /reports/csa-csi-kpi-report) takes precedence
  // over its parent (e.g. /reports).
  const sortedPermissions = [...permissionMap].sort(
    (a, b) => b.route.length - a.route.length
  );

  const matched = sortedPermissions.find((permission) =>
    currentUrl === permission.route || currentUrl.startsWith(permission.route + '/')
  );

  if (matched && !matched.allowedRoles.includes(userRole)) {
    let redirectTo: string;
    if (userRole === APP_CONSTANTS.ROLES.STUDENT) {
      redirectTo = APP_CONSTANTS.ROUTES.STUDENT_DASHBOARD;
    } else if (
      userRole === APP_CONSTANTS.ROLES.CareerServiceSupervisor &&
      currentUrl.startsWith(APP_CONSTANTS.ROUTES.REPORTS)
    ) {
      // CSS can only access the CSI/CSA KPI report under /reports; bounce any
      // other /reports* deny back to that report instead of /job.
      redirectTo = csiKpiReportUrl;
    } else if (
      isSalesTeamRole(userRole) ||
      userRole === APP_CONSTANTS.ROLES.CC ||
      userRole === APP_CONSTANTS.ROLES.TM
    ) {
      redirectTo = getDefaultRouteForRole(userRole);
    } else {
      redirectTo = APP_CONSTANTS.ROUTES.JOB;
    }
    router.navigate([redirectTo]);
    return false;
  }

  return true;
};
